import React, { useState, useEffect } from 'react';
import './AssessmentForm.css';

const reportID = '050B407B-7499-4182-8F80-4A341822C69C'; // Placeholder for report ID if needed

const PHQ9Component = ({ reportId, api, onSubmit , isPublished}) => {
  const [responses, setResponses] = useState({});
  const [reAssess, setReAssess] = useState(null);
  const [savingStates, setSavingStates] = useState({}); // { questionIndex: 'saving' | 'saved' | 'error' }
  const [saveTimeouts, setSaveTimeouts] = useState({}); // Track debounce timeouts
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const scaleLabels = [
    { value: 0, label: 'Not at all' },
    { value: 1, label: 'Several days' },
    { value: 2, label: 'More than half the days' },
    { value: 3, label: 'Nearly every day' }
  ];

  const questions = [
    "Little interest or pleasure in doing things",
    "Feeling down, depressed, or hopeless",
    "Trouble falling or staying asleep, or sleeping too much",
    "Feeling tired or having little energy",
    "Poor appetite or overeating",
    "Feeling bad about yourself - or that you are a failure or have let yourself or your family down",
    "Trouble concentrating on things, such as reading the newspaper or watching television",
    "Moving or speaking so slowly that other people could have noticed. Or the opposite - being so fidgety or restless that you have been moving around a lot more than usual",
    "Thoughts that you would be better off dead, or of hurting yourself in some way"
  ];

  const fieldNames = [
    "phq9_answer_1",
    "phq9_answer_2",
    "phq9_answer_3",
    "phq9_answer_4",
    "phq9_answer_5",
    "phq9_answer_6",
    "phq9_answer_7",
    "phq9_answer_8",
    "phq9_answer_9"
  ];

  const riskItem = 8; // Question 9 (index 8) - self-harm/suicidal thoughts

  // Load existing responses from API
  useEffect(() => {
    const loadExistingResponses = async () => {
      try {
        setLoading(true);
        const effectiveReportId = reportId || reportID;
        const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/sections/PHQ9Assessment`);

        if (response.ok) {
          const data = await response.json();

          // Convert field names back to question indexes
          const indexedResponses = {};
          fieldNames.forEach((fieldName, index) => {
            if (data.questions && data.questions[fieldName] !== undefined && data.questions[fieldName] !== null) {
              indexedResponses[index] = data.questions[fieldName];
            }
          });

          setResponses(indexedResponses);
          setReAssess(data.reAssess);

          // Mark all loaded responses as saved
          const savedStates = {};
          Object.keys(indexedResponses).forEach(index => {
            savedStates[index] = 'saved';
          });
          if (data.reAssess !== null && data.reAssess !== undefined) {
            savedStates['re_assess'] = 'saved';
          }
          setSavingStates(savedStates);
        }
      } catch (error) {
        console.error('Error loading existing responses:', error);
        setLoadError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadExistingResponses();
  }, []);

  // API call for saving individual response
  const saveResponse = async (questionIndex, value) => {
    if (isPublished) return;
    console.log(`Saving PHQ-9 response: Question ${questionIndex + 1}, Value: ${value}`);

    const fieldName = fieldNames[questionIndex];
    const effectiveReportId = reportId || reportID;

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/sections/PHQ9Assessment/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify({
          value: parseInt(value)
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSavingStates(prev => ({
        ...prev,
        [questionIndex]: 'saved'
      }));

    } catch (error) {
      console.error(`Error saving response for question ${questionIndex + 1}:`, error);
      setSavingStates(prev => ({
        ...prev,
        [questionIndex]: 'error'
      }));
    }
  };

  // API call for saving re-assess field
  const saveReAssess = async (value) => {
    if (isPublished) return;
    console.log(`Saving PHQ-9 re-assess: ${value}`);

    const effectiveReportId = reportId || reportID;

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/sections/PHQ9Assessment/fields/re_assess`, {
        method: 'PATCH',
        body: JSON.stringify({
          value: value
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSavingStates(prev => ({
        ...prev,
        're_assess': 'saved'
      }));

    } catch (error) {
      console.error('Error saving re-assess:', error);
      setSavingStates(prev => ({
        ...prev,
        're_assess': 'error'
      }));
    }
  };

  const handleResponseChange = (questionIndex, value) => {
    setResponses(prev => ({
      ...prev,
      [questionIndex]: parseInt(value)
    }));

    // Clear any existing timeout for this question
    if (saveTimeouts[questionIndex]) {
      clearTimeout(saveTimeouts[questionIndex]);
    }

    // Set saving state immediately
    setSavingStates(prev => ({
      ...prev,
      [questionIndex]: 'saving'
    }));

    // Debounce the API call
    const timeoutId = setTimeout(() => {
      saveResponse(questionIndex, value);
    }, 400);

    setSaveTimeouts(prev => ({
      ...prev,
      [questionIndex]: timeoutId
    }));
  };

  const handleReAssessChange = (value) => {
    setReAssess(value);

    // Clear any existing timeout
    if (saveTimeouts['re_assess']) {
      clearTimeout(saveTimeouts['re_assess']);
    }

    // Set saving state
    setSavingStates(prev => ({
      ...prev,
      're_assess': 'saving'
    }));

    // Debounce the API call
    const timeoutId = setTimeout(() => {
      saveReAssess(value);
    }, 400);

    setSaveTimeouts(prev => ({
      ...prev,
      're_assess': timeoutId
    }));
  };

  const calculateScore = () => {
    const completedItems = Object.keys(responses).length;

    if (completedItems !== questions.length) {
      return null; // Must answer all questions for valid score
    }

    const totalScore = questions.reduce((sum, _, index) => {
      return sum + (responses[index] || 0);
    }, 0);

    return totalScore;
  };

  const getSeverityLevel = (score) => {
    if (score === null) return null;
    if (score <= 4) return 'Minimal depression';
    if (score <= 9) return 'Mild depression';
    if (score <= 14) return 'Moderate depression';
    if (score <= 19) return 'Moderately severe depression';
    return 'Severe depression';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const score = calculateScore();
    if (score === null) {
      alert('Please complete all questions.');
      return;
    }

    const result = {
      assessmentType: 'PHQ-9',
      responses,
      reAssess,
      score,
      severity: getSeverityLevel(score),
      riskIndicator: responses[riskItem] > 0 ? 'Risk indicated - suicidal/self-harm thoughts reported' : 'No risk indicated',
      completedAt: new Date().toISOString(),
      timeframe: 'Past 2 weeks'
    };

    if (onSubmit) {
      await onSubmit(result);
    }
  };

  const completedCount = Object.keys(responses).length;
  const canSubmit = completedCount === questions.length;
  const currentScore = calculateScore();

  if (loading) {
    return (
      <div className="assessment-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading existing responses...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="assessment-container">
        <div className="error-state">
          <p>Error loading responses: {loadError}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="assessment-header">
        <h2>PHQ-9 Assessment</h2>
        <p><strong>Patient Health Questionnaire-9</strong></p>
        <p><strong>Instructions:</strong> Over the <strong>last 2 weeks</strong>, how often have you been bothered by any of the following problems?</p>
      </div>

      <div className="scale-header sticky-header">
        <div className="scale-labels phq9-scale-header">
          <div className="scale-label">
            {/* Empty space above question column */}
          </div>
          {scaleLabels.map(scale => (
            <div key={scale.value} className="scale-label phq9-scale" aria-label={`Scale option: ${scale.label}`}>
              <span className="scale-value">{scale.value}</span>
              <span className="scale-text">{scale.label}</span>
            </div>
          ))}
          <div className="scale-label save-status-header">
            <span className="scale-text">Status</span>
          </div>
        </div>
      </div>

      {responses[riskItem] > 0 && (
        <div className="risk-alert-banner">
          PLEASE COMPLETE RISK ASSESSMENT!
        </div>
      )}

      <form onSubmit={handleSubmit} className="assessment-form">
        <div className="questions-container phq9-grid">
          {questions.map((question, index) => (
            <fieldset
              key={index}
              className={`question-fieldset ${index === riskItem ? 'risk-question' : ''}`}
            >
              <legend className="question-legend">
                {index + 1}. {question}
                {index === riskItem && (
                  <span className="risk-indicator" aria-label="Risk assessment question">
                    ⚠️
                  </span>
                )}
              </legend>
              <div className="response-options phq9-options">
                {scaleLabels.map(scale => (
                  <div key={scale.value} className="radio-container">
                    <input
                      type="radio"
                      id={`phq9_${index}_${scale.value}`}
                      name={`phq9_question_${index}`}
                      value={scale.value}
                      checked={responses[index] === scale.value}
                      onChange={(e) => handleResponseChange(index, e.target.value)}
                      className="response-radio"
                    />
                    <label
                      htmlFor={`phq9_${index}_${scale.value}`}
                      className="radio-label sr-only"
                    >
                      {scale.label}
                    </label>
                  </div>
                ))}
              </div>
              <div className="save-status-container">
                {savingStates[index] === 'saving' && (
                  <div className="save-status saving" aria-label="Saving response">
                    <div className="spinner"></div>
                  </div>
                )}
                {savingStates[index] === 'saved' && (
                  <div className="save-status saved" aria-label="Response saved">
                    <span className="checkmark">✓</span>
                  </div>
                )}
                {savingStates[index] === 'error' && (
                  <div className="save-status error" aria-label="Save failed">
                    <span className="error-mark">⚠</span>
                  </div>
                )}
              </div>
      
      </fieldset>
          ))}
        </div>

        <div className="re-assess-section">
          <fieldset className="question-fieldset">
            <legend className="question-legend">
              If you checked off any problems, how difficult have these problems made it for you to do your work, take care of things at home, or get along with other people?
            </legend>
            <div className="re-assess-options">
              <div className="radio-container-block">
                <input
                  type="radio"
                  id="re_assess_not_difficult"
                  name="re_assess"
                  value="false"
                  checked={reAssess === false}
                  onChange={() => handleReAssessChange(false)}
                  className="response-radio"
                />
                <label htmlFor="re_assess_not_difficult" className="radio-label-block">
                  Not difficult at all
                </label>
              </div>
              <div className="radio-container-block">
                <input
                  type="radio"
                  id="re_assess_somewhat"
                  name="re_assess"
                  value="true"
                  checked={reAssess === true}
                  onChange={() => handleReAssessChange(true)}
                  className="response-radio"
                />
                <label htmlFor="re_assess_somewhat" className="radio-label-block">
                  Somewhat difficult / Very difficult / Extremely difficult
                </label>
              </div>
            </div>
            <div className="save-status-container">
              {savingStates['re_assess'] === 'saving' && (
                <div className="save-status saving" aria-label="Saving response">
                  <div className="spinner"></div>
                </div>
              )}
              {savingStates['re_assess'] === 'saved' && (
                <div className="save-status saved" aria-label="Response saved">
                  <span className="checkmark">✓</span>
                </div>
              )}
              {savingStates['re_assess'] === 'error' && (
                <div className="save-status error" aria-label="Save failed">
                  <span className="error-mark">⚠</span>
                </div>
              )}
            </div>
    
      </fieldset>
        </div>

        <div className="form-actions">
          <div className="completion-status">
            <div>Completed: {completedCount} / {questions.length}</div>
            {currentScore !== null && (
              <div className="score-display">
                <strong>Total Score: {currentScore}</strong>
                <div className="severity-level">{getSeverityLevel(currentScore)}</div>
              </div>
            )}
            {responses[riskItem] > 0 && (
              <div className="risk-alert">
                <strong>⚠️ Risk indicator detected - immediate clinical follow-up required</strong>
              </div>
            )}
          </div>
          <button
            type="submit"
            className="submit-button"
            disabled={!canSubmit}
          >
            Submit Assessment
          </button>
        </div>
      </form>

      <div className="assessment-info">
        <h3>About PHQ-9</h3>
        <ul>
          <li>Time frame: Past 2 weeks</li>
          <li>Score range: 0-27</li>
          <li>Severity levels:
            <ul>
              <li>0-4: Minimal depression</li>
              <li>5-9: Mild depression</li>
              <li>10-14: Moderate depression</li>
              <li>15-19: Moderately severe depression</li>
              <li>20-27: Severe depression</li>
            </ul>
          </li>
          <li>Question 9 assesses suicidal ideation - any positive response requires immediate clinical attention</li>
        </ul>
      </div>
      </fieldset>

    </div>
  );
};

export default PHQ9Component;
